import { Button } from '@components/ui';
import { ApiUrl } from '@config/apiUrl';
import apiProtected from '@lib/api/apiClient';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/material/styles';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { toast } from 'react-toastify';

import type { CouponDiscountResponse } from './verifyCouponTypes';


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

interface BootstrapDialogTitleProps {
  id?: string;
  children?: ReactNode;
  onClose?: () => void;
}

const BootstrapDialogTitle = ({
  children,
  onClose,
  ...other
}: BootstrapDialogTitleProps): JSX.Element => {
  const { t } = useTranslation();
  return (
    <DialogTitle sx={{ m: 0, p: 2, color: 'text.primary' }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label={t('common.close')}
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
};

interface AddCouponProps {
  handleAddCouponModal: () => void;
  open: boolean;
  cost?: string | number;
  handleCouponDiscountAmount: (response: CouponDiscountResponse) => void;
}

const AddCoupon = ({
  handleAddCouponModal,
  open,
  cost,
  handleCouponDiscountAmount,
}: AddCouponProps): JSX.Element => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState<string | undefined>(undefined);

  const handleClose = (): void => {
    handleAddCouponModal();
  };

  const handleCouponRes = (message?: string): void => {
    if (!message) {
      setLoading(false);
      handleAddCouponModal();
    } else {
      setLoading(false);
      toast.error(message);
    }
  };

  const handleSubmit = (): void => {
    setLoading(true);
    apiProtected
      .get<CouponDiscountResponse['data']>(
        ApiUrl.server_url + `v1/coupons/calculate-coupon-discount`,
        {
          params: { buyerCost: cost, code: couponCode },
        }
      )
      .then((res) => {
        handleCouponDiscountAmount(res);
        handleCouponRes();
      })
      .catch((error: unknown) => {
        const message =
          typeof error === 'object' && error !== null && 'response' in error
            ? (error as { response?: { data?: { message?: string } } }).response
                ?.data?.message
            : undefined;
        handleCouponRes(message);
      });
  };

  return (
    <Box sx={{ width: '100%' }}>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        fullWidth
      >
        <BootstrapDialogTitle
          id="customized-dialog-title"
          onClose={handleClose}
        >
          {t('hire.applyCoupon')}
        </BootstrapDialogTitle>
        <ValidatorForm style={{ width: '100%' }} onSubmit={() => handleSubmit()}>
          <Box component="hr" sx={{ mt: '-3px', borderColor: 'divider' }} />
          <DialogContent>
            <Grid container spacing={2} justifyContent="center" alignItems="center">
              <Grid item lg={12} md={12} sm={12} xs={12}>
                <Box sx={{ textAlign: 'center', fontWeight: 700, color: 'text.primary' }}>
                  {t('hire.couponCode')}
                </Box>
              </Grid>
              <Grid item lg={10} md={10} sm={12} xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextValidator
                    sx={{ width: '50%' }}
                    label={t('hire.enterVoucher')}
                    onChange={(event) => setCouponCode(event.target.value)}
                    type="text"
                    name="discount"
                    value={couponCode ?? ''}
                    validators={['required']}
                    errorMessages={['this field is required']}
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Box>
              </Grid>
              <Grid item lg={10} md={10} sm={12} xs={12}>
                <Button
                  variant="primary"
                  type="submit"
                  fullWidth
                  loading={loading}
                  sx={{ mb: 2 }}
                >
                  {t('common.submit')}
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
        </ValidatorForm>
      </BootstrapDialog>
    </Box>
  );
};

export default AddCoupon;
